import React, { useMemo, useState } from 'react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { Textarea } from '../../../shared/components/Textarea';
import type { EqualizationComparison, PurchaseQuotation } from '../api';
import { comparisonCurrencyGroups, formatMoney, referenceLabel } from './format';
import { selectionRequiresJustification } from '../validation/purchaseForms';

const EXCEPTION_REASONS = [
  ['exclusive_supplier', 'Fornecedor exclusivo'],
  ['emergency', 'Emergência'],
  ['renewal_continuity', 'Continuidade de renovação'],
  ['single_source_license', 'Licença de fonte única'],
  ['technical_compatibility', 'Compatibilidade técnica'],
  ['no_supplier_response', 'Sem retorno de fornecedores'],
  ['non_competitive', 'Mercado não competitivo'],
  ['framework_adherence', 'Aderência a contrato guarda-chuva'],
  ['other', 'Outro motivo'],
].map(([value, label]) => ({ value, label }));

interface Props {
  comparison?: EqualizationComparison;
  quotations: PurchaseQuotation[];
  loading?: boolean;
  error?: string | null;
  canSelect: boolean;
  canCreateException: boolean;
  submitting?: boolean;
  onSelect: (quotationId: string, justification?: string) => void;
  onCreateException: (reason: string, justification: string) => void;
}

export function EqualizationPanel({
  comparison,
  quotations,
  loading,
  error,
  canSelect,
  canCreateException,
  submitting,
  onSelect,
  onCreateException,
}: Props) {
  const [selectedQuotationId, setSelectedQuotationId] = useState('');
  const [selectionJustification, setSelectionJustification] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionJustification, setExceptionJustification] = useState('');

  const supplierLabels = useMemo(() => new Map(
    quotations.map((quotation) => [quotation.id, referenceLabel(quotation.supplier ?? { id: quotation.supplierId })]),
  ), [quotations]);
  const groups = useMemo(() => comparisonCurrencyGroups(comparison), [comparison]);
  const selectableGroups = groups.filter((group) => group.requirementSatisfied !== false || Boolean(comparison?.exception));
  const selectedGroup = selectableGroups.find((group) => group.global.some((entry) => entry.quotationId === selectedQuotationId));
  const requiresJustification = selectionRequiresJustification(selectedQuotationId, selectedGroup?.lowestQuotationId);

  if (loading) return <p className="text-sm text-gray-500">Carregando equalização...</p>;
  if (!comparison) {
    return (
      <div className="space-y-4">
        <Alert variant="info">A matriz ficará disponível quando houver cotações válidas para comparar.</Alert>
        {canCreateException && <ExceptionForm />}
      </div>
    );
  }

  function ExceptionForm() {
    const valid = exceptionReason && exceptionJustification.trim().length >= 10;
    return (
      <section className="space-y-3 rounded-md border border-amber-200 bg-amber-50/40 p-4" aria-labelledby="quotation-exception-title">
        <div>
          <h3 id="quotation-exception-title" className="font-medium text-gray-900">Exceção ao mínimo de cotações</h3>
          <p className="text-sm text-gray-600">A justificativa fica registrada na trilha de auditoria.</p>
        </div>
        <AutocompleteSelect
          label="Motivo da exceção *"
          options={EXCEPTION_REASONS}
          value={exceptionReason}
          onChange={setExceptionReason}
        />
        <Textarea
          label="Justificativa da exceção *"
          value={exceptionJustification}
          onChange={(event) => setExceptionJustification(event.target.value)}
          minLength={10}
          showCharCount
        />
        <Button
          loading={submitting}
          disabled={!valid}
          onClick={() => onCreateException(exceptionReason, exceptionJustification.trim())}
        >
          Registrar exceção
        </Button>
      </section>
    );
  }

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="rounded-full bg-gray-100 px-3 py-1">Mínimo: {comparison.minimumQuotes}</span>
        <span className="rounded-full bg-blue-100 px-3 py-1 text-blue-800">Válidas: {comparison.validQuotationCount}</span>
        {comparison.exception && <span className="rounded-full bg-amber-100 px-3 py-1 text-amber-800">Exceção registrada</span>}
      </div>

      {groups.map((group) => <section key={group.currency} className="space-y-3" aria-label={`Equalização em ${group.currency}`}>
        <div className="flex flex-wrap items-center gap-2"><h3 className="font-medium text-gray-900">Moeda {group.currency}</h3>{group.validQuotationCount != null && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700">{group.validQuotationCount} proposta(s)</span>}{group.requirementSatisfied === false && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-800">Mínimo ainda não atendido</span>}</div>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-left">Fornecedor</th>
              <th className="px-3 py-2 text-right">Quantidade</th>
              <th className="px-3 py-2 text-right">Valor unitário</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-left">Comparabilidade</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {group.matrix.flatMap((row) => [
              ...row.offers.map((offer, index) => (
              <tr key={`${row.requestItem.id}-${offer.quotationId}`} className={offer.isLowest ? 'bg-emerald-50' : ''}>
                <td className="px-3 py-2 font-medium text-gray-900">{index === 0 ? row.requestItem.description : ''}</td>
                <td className="px-3 py-2">{offer.supplierName ?? supplierLabels.get(offer.quotationId) ?? 'Fornecedor'}</td>
                <td className="px-3 py-2 text-right">{offer.quantity ?? '—'}</td>
                <td className="px-3 py-2 text-right">{formatMoney(offer.unitPrice, group.currency)}</td>
                <td className="px-3 py-2 text-right font-medium">{formatMoney(offer.totalValue, group.currency)}</td>
                <td className="px-3 py-2">{offer.comparable ? (offer.isLowest ? 'Menor valor' : 'Comparável') : (offer.nonComparableReason ?? 'Não comparável')}</td>
              </tr>
              )),
              ...(row.excludedOffers ?? []).map((offer, index) => (
                <tr key={`${row.requestItem.id}-${offer.quotationId}-excluded`} className="bg-gray-50 text-gray-600">
                  <td className="px-3 py-2 font-medium">{row.offers.length === 0 && index === 0 ? row.requestItem.description : ''}</td>
                  <td className="px-3 py-2">{offer.supplierName ?? supplierLabels.get(offer.quotationId) ?? 'Fornecedor cadastrado'}</td>
                  <td className="px-3 py-2 text-right">—</td><td className="px-3 py-2 text-right">—</td><td className="px-3 py-2 text-right">—</td>
                  <td className="px-3 py-2">Não comparável: {offer.reason ?? 'motivo registrado na proposta'}</td>
                </tr>
              )),
            ])}
          </tbody>
          </table>
        </div>
      </section>)}

      {canSelect && selectableGroups.some((group) => group.global.length > 0) && (
        <section className="space-y-3 rounded-md border border-gray-200 p-4" aria-labelledby="purchase-selection-title">
          <h3 id="purchase-selection-title" className="font-medium text-gray-900">Selecionar proposta</h3>
          <div className="space-y-2" role="radiogroup" aria-label="Propostas disponíveis">
            {selectableGroups.flatMap((group) => group.global.map((option) => (
              <label key={option.quotationId} className={`flex cursor-pointer items-center justify-between gap-4 rounded-md border p-3 ${selectedQuotationId === option.quotationId ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                <span className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="selected-quotation"
                    value={option.quotationId}
                    checked={selectedQuotationId === option.quotationId}
                    onChange={() => setSelectedQuotationId(option.quotationId)}
                  />
                  <span>{option.supplierName ?? supplierLabels.get(option.quotationId) ?? 'Fornecedor'}</span>
                  {option.isLowest && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">Menor valor</span>}
                </span>
                <strong>{formatMoney(option.totalValue, group.currency)}</strong>
              </label>
            )))}
          </div>
          <Textarea
            label={`Justificativa${requiresJustification ? ' *' : ' (opcional)'}`}
            value={selectionJustification}
            onChange={(event) => setSelectionJustification(event.target.value)}
            minLength={requiresJustification ? 10 : undefined}
            showCharCount
          />
          <Button
            loading={submitting}
            disabled={!selectedQuotationId || (requiresJustification && selectionJustification.trim().length < 10)}
            onClick={() => onSelect(selectedQuotationId, selectionJustification.trim() || undefined)}
          >
            Confirmar seleção
          </Button>
        </section>
      )}

      {canCreateException && !comparison.exception && <ExceptionForm />}
    </div>
  );
}
